import { definedMap } from "@glideapps/ts-necessities";
import { defaultAppFeatures } from "common/components/SerializedApp";
import type { AppLoginAuthData } from "common/Database";
import type { EminenceFlags } from "common/Database/eminence";
import { isWireApp } from "common/is-wire-app";
import { madeWithGlide } from "common/named-icons-lite";
import type { DeviceFormFactor } from "common/render/form-factor";
import { glideViralReferralLink } from "common/Support";
import { Appearance } from "generator/components/button-appearance";
import { withPlayerEminenceFlags } from "hooks/use-player-eminence-flags";
import { getAppKindFromPlay } from "lib/enzyme-utils";
import * as React from "react";
import type { FirebaseAppUserAuthenticator } from "sharedUI/lib/app-user-authenticator";
import { TailwindThemeProvider } from "sharedUI/lib/tailwind-theme-provider";

import { reloadBrowserWindow } from "../lib/browser-reload";
import AppContainer from "./chrome/smart/app-container/app-container";
import { getSpinner } from "./chrome/views/activity-spinner/activity-spinner";
import { GlideAppDesktopPlayer } from "./glide-app-desktop-player";
import { AppInfoWrapper, ConnectionIssueWrapper, PlayerContentPlaceholder } from "./glide-app-player.style";
import { getRuntimeThemeForPlatform, getWireAppThemeForPlatform } from "./lib/theme/theme";
import Button from "./ui/smart/button/button";

interface Props {
    useFrame: boolean;
    deviceFormFactor: DeviceFormFactor;
    showContentBlockMessage: boolean;
    checkContent: (content: React.ReactNode | undefined) => void; // REVIEW: This is kind of nasty
    id?: string;
    authenticator?: FirebaseAppUserAuthenticator;
    loginDataOrFallback?: AppLoginAuthData;
    eminenceFlags: EminenceFlags;
}
class GlideAppPlayerSkeletonImpl extends React.PureComponent<Props> {
    private showPlaceholder = (eminenceFlags: EminenceFlags, loginDataOrFallback?: AppLoginAuthData): JSX.Element => {
        let placeholder: JSX.Element;
        const MadeWithGlide = madeWithGlide;
        if (loginDataOrFallback !== undefined) {
            const { theme, features } = loginDataOrFallback;
            const { removeBranding } = eminenceFlags;
            const isWire = isWireApp(features);
            const platformTheme = isWire ? getWireAppThemeForPlatform(theme) : getRuntimeThemeForPlatform(theme);
            const { signInBackground } = theme;
            const showBackground =
                features.useCustomSignInBackgroundImageInLoading === true && signInBackground !== undefined;

            placeholder = (
                <TailwindThemeProvider theme={platformTheme}>
                    <PlayerContentPlaceholder backgroundImage={showBackground ? signInBackground : undefined}>
                        {!removeBranding && (
                            <a href={glideViralReferralLink("loading")} target="_blank" rel="noopener noreferrer">
                                <MadeWithGlide fgColor={platformTheme.fgColorDark} />
                            </a>
                        )}
                        <div className="spinner">
                            {getSpinner(platformTheme.primaryAccentColor, "loading-placeholder")}
                        </div>
                    </PlayerContentPlaceholder>
                </TailwindThemeProvider>
            );
        } else {
            // FIXME: Remove this flow when loginData can never be undefined. See https://github.com/quicktype/glide/issues/7271
            // I'm guessing this will never be undefined but added the check just in case
            const appLogin = (window as any).appLogin as AppLoginAuthData | undefined;
            const showBackground =
                appLogin !== undefined &&
                appLogin.features.useCustomSignInBackgroundImageInLoading === true &&
                appLogin.theme.signInBackground !== undefined;

            placeholder = (
                <PlayerContentPlaceholder
                    backgroundImage={
                        appLogin !== undefined && showBackground ? appLogin.theme.signInBackground : undefined
                    }>
                    <div className="spinner">{getSpinner("#000000", "loading-placeholder")}</div>
                </PlayerContentPlaceholder>
            );
        }

        return placeholder;
    };

    render() {
        const {
            useFrame,
            deviceFormFactor,
            showContentBlockMessage,
            checkContent,
            id,
            authenticator,
            loginDataOrFallback,
            eminenceFlags,
        } = this.props;

        let content = this.showPlaceholder(eminenceFlags, loginDataOrFallback);
        const isWire = isWireApp(loginDataOrFallback?.features);
        const platformTheme = isWire
            ? getWireAppThemeForPlatform({
                  primaryAccentColor: "#00AAFF",
                  showTabLabels: true,
                  themeOverlay: "none",
                  increaseContrast: false,
                  showDesktopSideBar: false,
              })
            : getRuntimeThemeForPlatform({
                  primaryAccentColor: "#00AAFF",
                  showTabLabels: true,
                  themeOverlay: "none",
                  increaseContrast: false,
                  showDesktopSideBar: false,
              });
        if (showContentBlockMessage) {
            content = (
                <TailwindThemeProvider theme={platformTheme}>
                    <ConnectionIssueWrapper>
                        <div>
                            Something prevented this app from running. Perhaps it was a connectivity issue or a content
                            blocker.
                        </div>
                        <Button
                            appearance={Appearance.Bordered}
                            url={undefined}
                            title="Try Again"
                            onClick={async () => {
                                reloadBrowserWindow("User requested reload");
                                return undefined;
                            }}
                            appKind={getAppKindFromPlay()}
                        />
                    </ConnectionIssueWrapper>
                </TailwindThemeProvider>
            );
        }

        checkContent(content);
        if (!useFrame) return content;

        const appContainer = (
            <AppContainer
                appFeatures={defaultAppFeatures}
                authenticator={authenticator}
                theme={definedMap(loginDataOrFallback, l => l.theme)}
                title={definedMap(loginDataOrFallback, l => l.title)}
                iconImage={definedMap(loginDataOrFallback, l => l.iconImage)}
                appID={id}
                forceTheme="iOS"
                isSplash={false}
                isBuilder={false}
                showBranding={false}
                showingApp={false}
                useFrame={true}
                deviceFormFactor={deviceFormFactor}>
                {content}
            </AppContainer>
        );

        const appInfo = <AppInfoWrapper />;

        return (
            <GlideAppDesktopPlayer
                showBranding={false}
                isSkeleton={true}
                appContainer={appContainer}
                appInfo={appInfo}
            />
        );
    }
}

export const GlideAppPlayerSkeleton = withPlayerEminenceFlags(GlideAppPlayerSkeletonImpl);
